import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ready = (callback: () => void): void => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }

  callback();
};

const setEnhancedState = (): void => {
  document.documentElement.classList.add('style-2026-js');
};

const enhanceSearch = (): void => {
  document
    .querySelectorAll<HTMLElement>('.style-2026-search')
    .forEach((search) => {
      const input = search.querySelector<HTMLInputElement>(
        'input[type="search"]'
      );

      if (!input) {
        return;
      }

      input.addEventListener('focus', () => search.classList.add('is-focused'));
      input.addEventListener('blur', () =>
        search.classList.remove('is-focused')
      );
    });
};

const alignMobileNotificationDropdown = (): void => {
  const contents = document.querySelector<HTMLElement>(
    '#notification_list .dropdown-contents'
  );

  if (!contents) {
    return;
  }

  if (!window.matchMedia('(width <= 650px)').matches) {
    contents.style.removeProperty('margin-left');
    return;
  }

  const profile = document.querySelector<HTMLElement>('.header-profile');

  if (!profile) {
    return;
  }

  const contentsRight = contents.getBoundingClientRect().right;
  const profileRight = profile.getBoundingClientRect().right;
  const rightOffset = contentsRight - profileRight;

  if (Math.abs(rightOffset) <= 1) {
    return;
  }

  const currentMarginLeft = Number.parseFloat(contents.style.marginLeft) || 0;
  contents.style.marginLeft = `${currentMarginLeft - rightOffset}px`;
};

const enhanceNotificationDropdown = (): void => {
  const notificationButton = document.querySelector<HTMLElement>(
    '#notification_list_button'
  );

  if (!notificationButton) {
    return;
  }

  let scheduledFrame: number | undefined;

  const alignAfterPhpbbToggle = (): void => {
    if (scheduledFrame !== undefined) {
      return;
    }

    scheduledFrame = window.requestAnimationFrame(() => {
      scheduledFrame = window.requestAnimationFrame(() => {
        scheduledFrame = undefined;
        alignMobileNotificationDropdown();
      });
    });
  };

  notificationButton.addEventListener('click', alignAfterPhpbbToggle);
  window.addEventListener('resize', alignAfterPhpbbToggle);
};

const enhanceDropdownAccessibility = (): void => {
  document
    .querySelectorAll<HTMLElement>('.dropdown-container')
    .forEach((container) => {
      const trigger = container.querySelector<HTMLElement>('.dropdown-trigger');

      if (!trigger) {
        return;
      }

      if (
        !trigger.matches('a[href], button, input, select, textarea, summary')
      ) {
        trigger.setAttribute('role', 'button');
        if (trigger.tabIndex < 0) {
          trigger.tabIndex = 0;
        }
        trigger.addEventListener('keydown', (event) => {
          if (
            !event.repeat &&
            (event.key === 'Enter' || event.key === ' ')
          ) {
            event.preventDefault();
            trigger.click();
          }
        });
      }

      const updateExpandedState = (): void => {
        trigger.setAttribute(
          'aria-expanded',
          container.classList.contains('dropdown-visible') ||
            container.classList.contains('visible')
            ? 'true'
            : 'false'
        );
      };

      trigger.setAttribute('aria-haspopup', 'true');
      updateExpandedState();

      new MutationObserver(updateExpandedState).observe(container, {
        attributeFilter: ['class']
      });
    });
};

const buildHeadingId = (heading: HTMLElement, index: number): string => {
  if (heading.id) {
    return heading.id;
  }

  const post = heading.closest<HTMLElement>('.style-2026-post');
  const postId = post?.id || 'topic';
  const id = `style-2026-${postId}-section-${index + 1}`;

  heading.id = id;
  return id;
};

const enhanceTopicToc = (): void => {
  const pageBody = document.querySelector<HTMLElement>(
    '.section-viewtopic .page-body'
  );
  const firstPost = document.querySelector<HTMLElement>(
    '.style-2026-post .content'
  );

  if (!pageBody || !firstPost) {
    return;
  }

  const headings = Array.from(
    firstPost.querySelectorAll<HTMLElement>(
      'h2.abbc3-heading, h3.abbc3-heading, h2[id], h3[id]'
    )
  ).filter((heading) => heading.textContent?.trim().length);

  if (headings.length < 3) {
    return;
  }

  const language = document.documentElement.lang || '';
  const title = language.startsWith('zh') ? '目录' : 'Contents';
  const toc = document.createElement('nav');
  const tocHeading = document.createElement('h3');
  const list = document.createElement('ol');

  toc.className = 'style-2026-topic-toc';
  toc.setAttribute('aria-label', title);
  tocHeading.textContent = title;
  toc.append(tocHeading);

  headings.forEach((heading, index) => {
    const item = document.createElement('li');
    const link = document.createElement('a');
    const headingId = buildHeadingId(heading, index);

    item.className =
      heading.tagName.toLowerCase() === 'h3' ? 'is-subsection' : 'is-section';
    link.href = `#${headingId}`;
    link.textContent = heading.textContent?.trim() || '';
    item.append(link);
    list.append(item);
  });

  toc.append(list);
  pageBody.insertBefore(
    toc,
    document.querySelector('.style-2026-action-bar.bar-top')?.nextSibling ||
      pageBody.firstChild
  );
};

const enhanceIndexMotion = (): void => {
  const recentList = document.querySelector<HTMLElement>('.recenttopic-list');

  if (
    !recentList ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const animateRow = (row: HTMLElement): void => {
    if (row.dataset.style2026Motion === 'true') {
      return;
    }

    row.dataset.style2026Motion = 'true';
    gsap.fromTo(
      row,
      { opacity: 0.72, y: 18, scale: 0.985 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: row,
          start: 'top 96%',
          end: 'top 74%',
          scrub: 0.35
        }
      }
    );

    const avatars = row.querySelectorAll<HTMLElement>(
      '.recenttopic-avatar img'
    );

    if (!avatars.length) {
      return;
    }

    gsap
      .timeline({
        scrollTrigger: {
          trigger: row,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      })
      .fromTo(
        avatars,
        { opacity: 0.48, scale: 0.82 },
        { opacity: 1, scale: 1, duration: 0.45, stagger: 0.04 }
      )
      .to(avatars, {
        opacity: 0.35,
        scale: 0.96,
        duration: 0.55,
        stagger: 0.04
      });
  };

  recentList
    .querySelectorAll<HTMLElement>('.recenttopic-row')
    .forEach(animateRow);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node.matches('.recenttopic-row')) {
          animateRow(node);
          return;
        }

        node
          .querySelectorAll<HTMLElement>('.recenttopic-row')
          .forEach(animateRow);
      });
    });

    ScrollTrigger.refresh();
  }).observe(recentList, { childList: true, subtree: true });
};

ready(() => {
  setEnhancedState();
  enhanceSearch();
  enhanceDropdownAccessibility();
  enhanceNotificationDropdown();
  enhanceTopicToc();
  enhanceIndexMotion();
});
